import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@saucelabs/ds-typography';
import { InputText, Toggle } from '@saucelabs/ds-inputs';
import { Button } from '@saucelabs/ds-button';
import validate from '@saucelabs/sl-util-validate';

import { selectors as groupsSelectors } from '../../group';
import selectors from '../selectors';
import operations from '../operations';

type ProxyInputValidation = {
  [key in 'host' | 'port']?: string;
};

const constraints = {
  host: {
    format: {
      // eslint-disable-next-line no-useless-escape
      pattern: '^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9\-]*[A-Za-z0-9])$',
      message: '^Invalid Hostname',
    },
    length: {
      minimum: 1,
      maximum: 253,
      message: '^Invalid Hostname',
    },
  },
  port: {
    numericality: {
      onlyInteger: true,
      greaterThanOrEqualTo: 1,
      lessThanOrEqualTo: 65535,
      message: '^Invalid Port',
    },
  },
};

const validateProxyInputs = (host, port) => (
  validate({ host, port }, constraints) as ProxyInputValidation
);

const Proxy: React.FC = () => {
  const dispatch = useDispatch<ReduxDispatcher>();
  const settings = useSelector(groupsSelectors.currentGroupSettingsSelector);
  const currentGroup = useSelector(groupsSelectors.currentGroupSelector);
  const isLoading = useSelector(selectors.isSettingsLoadingSelector);
  const [isEnabled, setIsEnabled] = useState(settings?.proxy_enabled ?? false);
  const [host, setHost] = useState(settings?.proxy?.host ?? '');
  const [port, setPort] = useState(settings?.proxy?.port ? settings?.proxy.port.toString() : '');
  const [inputErrors, setInputErrors] = useState<ProxyInputValidation>(
    validateProxyInputs(host, port) ?? {},
  );

  useEffect(() => {
    setIsEnabled(settings?.proxy_enabled ?? false);
    setHost(settings?.proxy?.host ?? '');
    setPort(settings?.proxy?.port ? settings?.proxy.port.toString() : '');
  }, [settings]);

  useEffect(() => {
    setInputErrors(validateProxyInputs(host, port) ?? {});
  }, [host, port]);

  const updateProxyEnabledSetting = useCallback((enabled) => {
    setIsEnabled(enabled);

    if (currentGroup && host && port && (settings?.proxy_enabled !== enabled)) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          proxy_enabled: isEnabled,
          proxy: settings?.proxy,
        },
        settingsName: 'proxy',
        updatedValues: {
          proxy_enabled: enabled,
          proxy: { host, port: parseInt(port, 10) },
        },
      }));
    }
  }, [currentGroup, dispatch, isEnabled, host, port, settings]);

  const updateProxySettings = useCallback(() => {
    if (currentGroup) {
      void dispatch(operations.updateSettings({
        groupId: currentGroup.id,
        originalValues: {
          proxy_enabled: isEnabled,
          proxy: settings?.proxy,
        },
        settingsName: 'proxy',
        updatedValues: {
          proxy_enabled: isEnabled,
          proxy: { host, port: parseInt(port, 10) },
        },
      }));
    }
  }, [currentGroup, dispatch, settings, host, port, isEnabled]);

  return (
    <>
      <div className="flex justify-between mt-2">
        <Typography className="text-high-emphasis font-semibold">
          Proxy
        </Typography>
        <Toggle
          isDisabled={isLoading}
          isSelected={isEnabled}
          onChange={updateProxyEnabledSetting}
        />
      </div>

      <div className="flex justify-between gap-2 mt-0.5">
        <InputText
          aria-label="Host"
          data-qa="app-management-settings-proxy-input-host"
          errorMessage={host ? inputErrors.host : ''}
          isDisabled={isLoading || !isEnabled}
          label=""
          onChange={setHost}
          placeholder="Host"
          value={host}
        />

        <InputText
          aria-label="Port"
          className="w-10"
          data-qa="app-management-settings-proxy-input-port"
          errorMessage={port ? inputErrors.port : ''}
          isDisabled={isLoading || !isEnabled}
          label=""
          onChange={setPort}
          placeholder="Port"
          value={port}
        />

        <Button
          disabled={isLoading || !isEnabled || !!validateProxyInputs(host, port)}
          onPress={updateProxySettings}
        >
          Update
        </Button>
      </div>
    </>
  );
};

export default Proxy;
